/*
 * The ParlayEscalator half of the prize-tier modal: Caesars' Parlay Escalator Token, which ships
 * as their `TieredProfitBoost` (`docs/reference/caesars-api/parlay-escalator-payload.md`) — a
 * profit boost whose percentage climbs with the parlay's leg count.
 *
 * It owns the whole reward type: the draft an operator edits, what counts as valid, and the cell
 * that gets stored. The modal holds one `EscalatorDraft` and calls the three functions here, so
 * adding a field to the escalator never means touching the modal.
 */
import { Button, Input, Select } from '@cou/admin-ui';
import type { BoostLadderRung, RewardCell } from './reward-cell';

/** A ladder rung mid-edit. Held as strings because that is what the inputs carry; converted to
 * numbers only on save, so a half-typed rung is never `NaN` in the draft. */
export interface RungDraft {
  legs: string;
  operator: string;
  percent: string;
}

export interface EscalatorDraft {
  maxStake: string;
  maxReward: string;
  minOddsPerLeg: string;
  oddsMin: string;
  ladder: RungDraft[];
}

const BLANK_RUNG: RungDraft = { legs: '', operator: '=', percent: '' };

/** `=` pays at exactly that leg count, `>=` at that many or more — the top rung is the `>=` one. */
const LADDER_OPERATORS = ['=', '>='];

/** The draft an operator starts from: a stored cell's config, or one blank rung for a new cell. */
export function toEscalatorDraft(cell: RewardCell | null): EscalatorDraft {
  const ladder = cell?.boostPercentageLadder;
  return {
    maxStake: cell?.maxStake != null ? String(cell.maxStake) : '',
    maxReward: cell?.maxReward != null ? String(cell.maxReward) : '',
    minOddsPerLeg: cell?.minOddsPerLeg ?? '',
    oddsMin: cell?.odds?.min ?? '',
    ladder:
      ladder && ladder.length > 0
        ? ladder.map((rung) => ({
            legs: String(rung.legs),
            operator: rung.operator,
            percent: String(rung.percent),
          }))
        : [{ ...BLANK_RUNG }],
  };
}

/**
 * What an escalator cell must carry to be deliverable, as operator-facing problems — empty when
 * the draft is good to save. A cell holding only its type builds a `prizeInfo` with no config,
 * which Caesars' award webhook rejects outright: five staging awards failed exactly that way
 * (CAE-564).
 *
 * `maxStake` is the only field Caesars requires, but a ladder with no rungs boosts nothing, so
 * this is deliberately stricter than the wire contract the mock encodes.
 */
export function escalatorProblems(draft: EscalatorDraft): string[] {
  const problems: string[] = [];
  if (draft.maxStake.trim() === '') problems.push('Max stake is required.');
  if (draft.ladder.length === 0) {
    problems.push('Add at least one boost-ladder rung.');
  } else if (draft.ladder.some((r) => r.legs.trim() === '' || r.percent.trim() === '')) {
    // A half-filled rung would serialise as `NaN`, which is not valid JSON.
    problems.push('Every rung needs both a leg count and a boost percent.');
  }
  return problems;
}

/**
 * The stored cell for a valid draft. Game-shaped, not wire-shaped: numbers stay numbers and the
 * pump stringifies them when it builds prizeInfo (CAE-556). A blank optional field is omitted
 * rather than sent — `maxReward: 0` would read as a cap of nothing, not as no cap at all.
 */
export function toEscalatorCell(draft: EscalatorDraft): RewardCell {
  const ladder: BoostLadderRung[] = draft.ladder.map((rung) => ({
    legs: Number(rung.legs),
    operator: rung.operator,
    percent: Number(rung.percent),
  }));
  return {
    type: 'ParlayEscalator',
    maxStake: Number(draft.maxStake),
    ...(draft.maxReward ? { maxReward: Number(draft.maxReward) } : {}),
    ...(draft.minOddsPerLeg ? { minOddsPerLeg: draft.minOddsPerLeg } : {}),
    ...(draft.oddsMin ? { odds: { min: draft.oddsMin } } : {}),
    boostPercentageLadder: ladder,
  };
}

export interface ParlayEscalatorFieldsProps {
  readonly draft: EscalatorDraft;
  readonly onChange: (draft: EscalatorDraft) => void;
  /** Shown above the fields after a rejected save; empty until the operator tries one. */
  readonly problems: readonly string[];
}

export function ParlayEscalatorFields({ draft, onChange, problems }: ParlayEscalatorFieldsProps) {
  const patch = (fields: Partial<EscalatorDraft>) => {
    onChange({ ...draft, ...fields });
  };
  const patchRung = (index: number, fields: Partial<RungDraft>) => {
    patch({ ladder: draft.ladder.map((rung, i) => (i === index ? { ...rung, ...fields } : rung)) });
  };

  return (
    <>
      {problems.length > 0 && (
        <div role="alert" className="col-span-2 flex flex-col gap-1">
          {problems.map((problem) => (
            <span key={problem} className="font-ui text-[12.5px] text-danger">
              {problem}
            </span>
          ))}
        </div>
      )}
      <Input
        label="Max stake"
        mono
        type="number"
        required
        value={draft.maxStake}
        onChange={(event) => patch({ maxStake: event.target.value })}
      />
      <Input
        label="Max reward"
        mono
        type="number"
        placeholder="—"
        value={draft.maxReward}
        onChange={(event) => patch({ maxReward: event.target.value })}
      />
      <Input
        label="Min odds per leg"
        mono
        placeholder="1/25"
        hint="Fractional"
        value={draft.minOddsPerLeg}
        onChange={(event) => patch({ minOddsPerLeg: event.target.value })}
      />
      <Input
        label="Min total odds"
        mono
        placeholder="—"
        hint="Whole parlay"
        value={draft.oddsMin}
        onChange={(event) => patch({ oddsMin: event.target.value })}
      />
      <div className="col-span-2 flex flex-col gap-2">
        <span className="font-ui text-[11px] font-medium uppercase tracking-[0.06em] text-ink-4">
          Boost ladder
        </span>
        {/* Column captions ride above the rungs rather than on the first one: admin-ui's Input
            renders its hint *below* the control, so a per-field hint on row 1 reads as an
            annotation on that rung instead of a heading for the column. */}
        {draft.ladder.length > 0 && (
          <div
            aria-hidden
            className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 font-ui text-[11px] font-medium uppercase tracking-[0.06em] text-ink-4"
          >
            <span>Legs</span>
            <span>Operator</span>
            <span>Boost %</span>
            <span className="w-[30px]" />
          </div>
        )}
        {draft.ladder.map((rung, index) => (
          <div
            // Rungs have no id and are reordered only by removal, which rewrites every later
            // input's value from the draft — so the index is a stable enough key here.
            key={index}
            className="grid grid-cols-[1fr_1fr_1fr_auto] items-end gap-2"
            role="group"
            aria-label={`Ladder rung ${String(index + 1)}`}
          >
            <Input
              aria-label={`Legs, rung ${String(index + 1)}`}
              mono
              type="number"
              value={rung.legs}
              onChange={(event) => patchRung(index, { legs: event.target.value })}
            />
            <Select
              aria-label={`Operator, rung ${String(index + 1)}`}
              options={LADDER_OPERATORS}
              value={rung.operator}
              onChange={(operator) => patchRung(index, { operator })}
            />
            <Input
              aria-label={`Boost percent, rung ${String(index + 1)}`}
              mono
              type="number"
              value={rung.percent}
              onChange={(event) => patchRung(index, { percent: event.target.value })}
            />
            <button
              type="button"
              aria-label={`Remove rung ${String(index + 1)}`}
              className="h-9 rounded-md px-2 font-ui text-[12.5px] text-ink-4 hover:bg-surface-muted hover:text-danger"
              onClick={() => patch({ ladder: draft.ladder.filter((_, i) => i !== index) })}
            >
              ✕
            </button>
          </div>
        ))}
        <div>
          <Button
            variant="ghost"
            onClick={() => patch({ ladder: [...draft.ladder, { ...BLANK_RUNG }] })}
          >
            + Add rung
          </Button>
        </div>
      </div>
    </>
  );
}
